import { useQuery } from '@tanstack/react-query';
import { linkOptions, useLocation, useParams } from '@tanstack/react-router';

import { Breadcrumbs } from '../../cdl/Breadcrumbs/Breadcrumbs';
import { Text } from '../../cdl/Text/Text';
import { queries } from '../../common/api/queryKeys/queries';
import { AdvancedError } from '../../common/Error/AdvancedError';
import { translate } from '../../common/helpers/translate.helper';
import { useDocumentTitle } from '../../common/hooks/useDocumentTitle';
import { useRole } from '../../common/hooks/useRole';
import { LoadingIndicator } from '../../common/LoadingIndicator/LoadingIndicator';
import { Page } from '../../common/Page/Page';
import { PageGrid } from '../../common/PageGrid';
import { Box } from '../../common/ui/Box';
import { ContractDataSheet } from '../../contracts/detail/components/ContractDataSheet';
import { ContractDetailActions } from '../../contracts/detail/components/ContractDetailActions';
import { ContractDetailsSection } from '../../contracts/detail/components/ContractDetailsSection';
import { ContractPriceListsSection } from '../../contracts/detail/components/ContractPriceListsSection';
import { PRICE_LIST_MIN_WIDTH } from '../../contracts/detail/priceList.constants';
import { ContractStatusTag } from '../../contracts/overview/components/ContractStatusTag';
import { VesselDetailPageTabs } from '../../vessel/VesselOverview/components/VesselDetailPageContent';

const DETAILS_COLUMN_WIDTH = 280;
const DETAIL_SECTION_GAP = 48;
const DETAIL_CONTENT_MIN_WIDTH_PX = PRICE_LIST_MIN_WIDTH + DETAILS_COLUMN_WIDTH + DETAIL_SECTION_GAP;

export const ContractDetailPage = () => {
    const role = useRole();
    const { id } = useParams({ from: '/_app/contracts/$id' });
    const location = useLocation();
    const contractDetailQuery = useQuery(queries.contracts.detail(id));

    useDocumentTitle(
        contractDetailQuery.data?.name
            ? translate('contracts.detail.pageTitle', {
                  name: contractDetailQuery.data?.name,
              })
            : translate('contracts.detail.pageTitleContract')
    );

    if (contractDetailQuery.isPending) {
        return (
            <Page>
                <LoadingIndicator variant="page" />
            </Page>
        );
    }

    if (contractDetailQuery.isError) {
        return (
            <Page>
                <AdvancedError />
            </Page>
        );
    }

    const contract = contractDetailQuery.data;
    const priceLists = contract.priceLists;
    const sourceVesselId = location.state?.vesselId;
    const isContractAdmin = role.hasAnyAdminRights();
    const breadcrumbLink = sourceVesselId
        ? {
              label: translate('vessel.contracts.title'),
              linkOptions: linkOptions({
                  to: '/vessel/$id',
                  params: { id: sourceVesselId },
                  search: { tab: VesselDetailPageTabs.CONTRACTS },
              }),
          }
        : {
              label: translate(isContractAdmin ? 'contracts.headline' : 'navigation.vessels'),
              linkOptions: isContractAdmin ? linkOptions({ to: '/contracts' }) : linkOptions({ to: '/vessels' }),
          };

    return (
        <Page>
            <PageGrid>
                <Box display="flex" flexDirection="column" gap={16}>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={16}>
                        <Box display="flex" flexDirection="column" gap={8}>
                            <Breadcrumbs links={[breadcrumbLink]} />

                            <Box display="flex" alignItems="center" gap={8}>
                                <Text variant="headline" as="h1">
                                    {contract.name}
                                </Text>

                                <ContractStatusTag status={contract.state} />
                            </Box>
                        </Box>

                        {isContractAdmin ? <ContractDetailActions contract={contract} /> : null}
                    </Box>

                    <Box
                        display="grid"
                        gridTemplateColumns={`minmax(${PRICE_LIST_MIN_WIDTH}px, 1fr) ${DETAILS_COLUMN_WIDTH}px`}
                        alignItems="flex-start"
                        gap={DETAIL_SECTION_GAP}
                        width="100%"
                        minWidth={`${DETAIL_CONTENT_MIN_WIDTH_PX}px`}
                    >
                        <Box display="flex" flexDirection="column" gap={16} minWidth={0}>
                            <Text variant="title" as="h2">
                                {translate('contracts.detail.overview')}
                            </Text>

                            <Box display="flex" flexDirection="column" gap={32}>
                                <ContractDataSheet contract={contract} />
                                <ContractPriceListsSection contract={contract} priceLists={priceLists} />
                            </Box>
                        </Box>

                        <Box display="flex" flexDirection="column" gap={16}>
                            <Text variant="title" as="h2">
                                {translate('contracts.detail.details')}
                            </Text>

                            <ContractDetailsSection contract={contract} />
                        </Box>
                    </Box>
                </Box>
            </PageGrid>
        </Page>
    );
};
